<template>
  <div v-if="donation">
    <!-- Form -->
    <b-form>
      <b-row v-if="!donation.isRelated">
        <!-- Field: Issue date -->
        <b-col
          cols="12"
          md="6"
          lg="3"
        >
          <b-form-group
            :label="$t('donations.issue-date'+ (isGiverMode ? '-giver' : ''))"
            label-for="issue-date"
          >
            <date-picker-input
              id="issue-date"
              v-model="donation.issueDate"
            />
          </b-form-group>
        </b-col>
        <!-- Field: Amount -->
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            :label="$t('donations.amount')"
            label-for="amount"
          >
            <currency-input
              id="amount"
              v-model="donation.amount"
              class="form-control"
              placeholder="0,00"
            />
          </b-form-group>
        </b-col>
        <!-- Field: Currency -->
        <b-col
          cols="12"
          md="6"
          lg="4"
        >
          <b-form-group
            :label="$t('donations.currency')"
            label-for="currency"
            label-class="mb-1"
          >
            <b-form-radio-group
              id="currency"
              v-model="donation.currency"
              :options="currencyOptions"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <!-- Field: Contributor Name -->
        <b-col
          v-if="!isGiverMode"
          cols="12"
          md="6"
          lg="6"
        >
          <b-form-group
            :label="$t('donations.contributor-name')"
            label-for="contributor-name"
          >

            <b-input-group>
              <b-form-input
                disabled
                :value="(contributorFullName ? contributorFullName : $t('donations.filter-contributor'))"
              />
              <b-input-group-append v-if="allowSelectContributors">

                <b-button
                  variant="outline-primary"
                  @click="searchContributors()"
                >
                  <feather-icon icon="SearchIcon" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>

        <!-- Field: Project -->
        <b-col
          v-if="!isGiverMode"
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('donations.project-name')"
            label-for="project-name"
          >
            <b-input-group>
              <b-form-input
                disabled
                :value="(projectFullName ? projectFullName : $t('donations.filter-project'))"
              />
              <b-input-group-append v-if="allowSelectProjects">

                <b-button
                  variant="outline-primary"
                  @click="searchProjects()"
                >
                  <feather-icon icon="SearchIcon" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="!donation.isRelated">
        <!-- Givers Type Filter -->
        <b-col
          v-if="isGiverMode"
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('donations.givers-type')"
            label-for="giversTypeFilter"
          >
            <v-select
              v-model="donation.infos.giversType"
              :options="lookupOptions.giversTypeOptions"
              taggable
              push-tags
              :placeholder="$t('donations.filter-givers-type')"
              :reduce="val => val.label"
            />
          </b-form-group>
        </b-col>
        <!-- Payments Filter -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('donations.filter-payments-label')"
            label-for="paymentsTypeFilter"
          >
            <v-select
              v-model="donation.infos.payments"
              :options="lookupOptions.paymentsTypeOptions"
              taggable
              push-tags
              :placeholder="$t('donations.filter-payments')"
              :reduce="val => val.label"
            />
          </b-form-group>
        </b-col>

        <!-- Donations Type Filter -->
        <b-col
          v-if="!isGiverMode"
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('donations.filter-donations-label')"
            label-for="donationsTypeFilter"
          >
            <v-select
              v-model="donation.infos.type"
              :options="lookupOptions.donationsTypeOptions"
              taggable
              push-tags
              :placeholder="$t('donations.filter-donations')"
              :reduce="val => val.label"
            />
          </b-form-group>
        </b-col>

        <!-- Category Type Filter -->
        <b-col
          v-if="!isGiverMode"
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('donations.filter-category-label')"
            label-for="categoryFilter"
          >
            <v-select
              v-model="donation.infos.category"
              :options="lookupOptions.categoryOptions"
              taggable
              push-tags
              :placeholder="$t('donations.filter-category')"
              :reduce="val => val.label"
            />
          </b-form-group>
        </b-col>

        <!-- Field: Nation  -->
        <b-col
          v-if="!isGiverMode"
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('projects.filter-nations-label')"
            label-for="nation"
          >
            <v-select
              v-model="donation.infos.nation"
              :options="lookupOptions.nationsOptions"
              :reduce="val => val.label"
              :placeholder="$t('donations.filter-nation')"
              taggable
              push-tags
              input-id="nation"
            />
          </b-form-group>
        </b-col>

        <b-col
          v-if="!isGiverMode"
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('projects.filter-tags-label')"
            label-for="tags"
          >
            <div class="d-flex align-items-center justify-content-end">
              <tags-selector
                v-model="donation.tags"
                :taggable="true"
                :tags-options="lookupOptions.tagsOptions"
                :tags-colors="lookupOptions.tagsColors"
              />
            </div>
          </b-form-group>
        </b-col>

        <b-col
          v-if="isGiverMode"
          cols="12"
          md="3"
        >
          <b-form-group
            :label="$t('donations.balance-amount')"
            label-for="balance-amount"
          >
            <currency-input
              id="balance-amount"
              v-model="donation.infos.balanceAmount"
              class="form-control"
              placeholder="0,00"
            />
          </b-form-group>
        </b-col>
        <!-- Field: Currency -->
        <b-col
          v-if="isGiverMode"
          cols="12"
          md="6"
          lg="4"
        >
          <b-form-group
            :label="$t('donations.balance-currency')"
            label-for="balance-currency"
            label-class="mb-1"
          >
            <b-form-radio-group
              id="balance-currency"
              v-model="donation.infos.balanceCurrency"
              :options="currencyOptions"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          cols="12"
          xl="12"
          md="12"
        >
          <b-form-group
            :label="$t('common.notes')"
            label-for="notes"
          >
            <div class="form-label-group ">
              <quill-editor
                id="notes"
                v-model="donation.infos.notes"
                class="quill"
                :options="quillEditorOptions"
              />
            </div>
          </b-form-group>
        </b-col>
        <b-col
          v-if="donation.did"
          cols="12"
          md="12"
        >
          <feather-icon
            icon="Edit3Icon"
            class="mr-75"
          />
          <span class="font-weight-bold mr-75">Ultima modifica:</span>
          {{ `${formatDateHumanFriendly(donation.lastUpdate)} - ${donation.editor}` }}
        </b-col>
      </b-row>
    </b-form>

    <projects-modal
      ref="projectsModal"
    />
    <contributors-modal
      ref="contributorsModal"
    />
  </div>
</template>

<script>
/* eslint-disable import/no-extraneous-dependencies */
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
/* eslint-disable vue/require-prop-types */
import {
  BForm, BFormGroup, BFormInput, BRow, BCol, BFormRadioGroup, BInputGroup, BInputGroupAppend, BButton,
} from 'bootstrap-vue'
import { quillEditor } from 'vue-quill-editor'
import vSelect from 'vue-select'
import lookupsService from '@/vue.lookups.service'
import ProjectsModal from '@/views/projects/ProjectsModal.vue'
import ContributorsModal from '@/views/contributors/ContributorsModal.vue'
import DatePickerInput from '../layouts/DatePickerInput.vue'
import TagsSelector from '../layouts/tables/TagsSelector.vue'
import CurrencyInput from '../layouts/CurrencyInput.vue'
import {
  avatarText, resolveAvatarVariant, formatAddress, formatAge, formatDate, formatDateHumanFriendly, quillEditorOptions,
} from '../layouts/layouts.utils'

export default {
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    quillEditor,
    BRow,
    BCol,
    BFormRadioGroup,
    DatePickerInput,
    BInputGroup,
    BInputGroupAppend,
    BButton,
    vSelect,
    ProjectsModal,
    ContributorsModal,
    CurrencyInput,
    TagsSelector,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    contributor: {
      type: Object,
      default: null,
    },
    project: {
      type: Object,
      default: null,
    },
    isGiverMode: {
      required: false,
      default: false,
    },
    refaid: {
      required: true,
    },
    lookupOptions: {
      type: Object,
      default: null,
    },
    allowSelectProjects: {
      required: false,
      default: false,
    },
    allowSelectContributors: {
      required: false,
      default: false,
    },
  },
  data() {
    return {
      projectSelected: null,
      contributorSelected: null,

    }
  },
  computed: {
    donation: {
      get() { return this.value },
      set(value) { this.$emit('input', value) },
    },
    projectFullName() {
      const item = (this.projectSelected ? this.projectSelected : this.project)
      if (item) {
        return `${item.projectName} | ${item.infos.birthDate ? `${formatDate(item.infos.birthDate, 'yyyy')} (${formatAge(item.infos.birthDate)} anni) / ` : ''}${item.infos.missionName ? item.infos.missionName : ''}`
      }
      return null
    },
    contributorFullName() {
      const item = (this.contributorSelected ? this.contributorSelected : this.contributor)
      if (item) {
        return `${item.fullName} | ${item.infos ? formatAddress(item.infos.address, true) : formatAddress(item.address, true)}`
      }
      return null
    },
  },
  methods: {
    searchProjects() {
      this.$refs.projectsModal.show(item => {
        this.donation.pid = item.pid
        this.projectSelected = item
        this.$refs.projectsModal.cancel()
      })
    },
    searchContributors() {
      this.$refs.contributorsModal.show(item => {
        this.donation.cid = item.cid
        this.contributorSelected = item
        this.$refs.contributorsModal.cancel()
      })
    },
  },
  setup() {
    const currencyOptions = lookupsService.getCurrencyOptions()

    return {
      currencyOptions,
      avatarText,
      resolveAvatarVariant,
      formatAddress,
      formatDateHumanFriendly,
      quillEditorOptions,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
